import { useState, useRef, useEffect, useLayoutEffect, useMemo, useReducer } from 'react';
import { Upload, Film, Info, CheckCircle, Copy, Check, X } from 'react-feather';

import { useRemote } from '../Utils/Utils';
import { toastSuccess, toastWarning } from '../components/popups/Toast';
import LoadingIcon from '../components/LoadingIcon';
import Modal from '../components/Modals/Modal';
import { uploadVideoToBunny, validateVideoFile, ACCEPTED_VIDEO_ACCEPT_ATTR } from '../Utils/bunnyUpload';
import './VideoUploadPanel.scss';

const DEV_VIDEO_URL = 'https://www.w3schools.com/html/mov_bbb.mp4';
const DEV_THUMBNAILS = [
    'https://picsum.photos/seed/thumb1/160/90',
    'https://picsum.photos/seed/thumb2/160/90',
    'https://picsum.photos/seed/thumb3/160/90',
];
const DEV_TRANSCRIPTION = `This quarter the Lonsec Active Balanced Portfolio delivered strong risk-adjusted returns, outperforming the benchmark by 1.2%. The portfolio benefited from an overweight position in domestic equities and reduced exposure to fixed income as rates rose. Looking ahead, we remain cautiously optimistic and are monitoring global inflation data closely.

Equity markets continued to show resilience despite ongoing macroeconomic headwinds, including elevated interest rates and persistent inflationary pressures in key global economies. Our domestic equity sleeve contributed meaningfully to performance, driven by selective exposure to high-quality financials and healthcare names that demonstrated earnings stability throughout the period.

Within fixed income, we maintained a shorter duration profile relative to the benchmark, which proved beneficial as yields moved higher through the quarter. Credit spreads remained broadly stable, and our allocation to investment-grade corporate bonds provided a steady income contribution without adding undue risk to the overall portfolio.

Looking at the quarter ahead, we are closely monitoring central bank policy trajectories, particularly in the United States and Australia, where the timing of any rate cuts will have meaningful implications for both equity valuations and bond yields. We believe the portfolio is well-positioned to navigate continued volatility, with a diversified asset mix and a focus on quality across all asset classes.`;

function transcriptionToSlate(text) {
    return JSON.stringify(
        text.split('\n').filter(Boolean).map(line => ({
            type: 'paragraph',
            children: [{ text: line }]
        }))
    );
}

function pushTranscriptionToEditor(data, transcription, templateObj) {
    if (!data || !transcription) return;
    // Try to find the first EditableText field from the template object
    let fieldName;
    if (templateObj && templateObj.pages) {
        const allContent = templateObj.pages.flatMap(p => p.content || []);
        const findField = (items) => {
            for (const item of items) {
                if ((item.className === 'EditableText' || item.className === 'FinalisedEditableText') && item.dataName) {
                    return item.dataName;
                }
                if (item.content) {
                    const found = findField(item.content);
                    if (found) return found;
                }
            }
        };
        fieldName = findField(allContent);
    }
    // Fallback: use the first key already in localContentData
    if (!fieldName && data.localContentData) {
        fieldName = Object.keys(data.localContentData)[0];
    }
    if (!fieldName) {
        toastWarning('Could not find a text field to populate.');
        return;
    }
    data.updateLocalData(fieldName, transcriptionToSlate(transcription));
    toastSuccess('Transcription sent to editor.');
}

function ReplicateButton({ options, onConfirm, preSelectedFundNumbers, template, groups: groupsProp }) {
    const makeInitialChecked = () => options.map(o =>
        preSelectedFundNumbers ? preSelectedFundNumbers.includes(o.fundNumber) : false
    );
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(makeInitialChecked);
    const [checkedGroups, setCheckedGroups] = useState([]);
    const hideTimeout = useRef();

    const groups = groupsProp || [];

    useEffect(() => {
        setChecked(options.map(o =>
            preSelectedFundNumbers ? preSelectedFundNumbers.includes(o.fundNumber) : false
        ));
    }, [options, preSelectedFundNumbers]);

    useEffect(() => {
        return () => { if (hideTimeout.current) clearTimeout(hideTimeout.current); };
    }, []);

    function handleMouseEnter() {
        if (hideTimeout.current) { clearTimeout(hideTimeout.current); hideTimeout.current = null; }
        setOpen(true);
    }

    function handleMouseLeave() {
        hideTimeout.current = setTimeout(() => setOpen(false), 200);
    }

    function handleConfirm() {
        const selectedPortfolios = options.filter((_, i) => checked[i]);
        // Expand checked groups into their member portfolios (deduplicated)
        const groupMembers = checkedGroups.flatMap(gid => {
            const g = groups.find(gr => gr.id === gid);
            if (!g) return [];
            return options.filter(o => g.fundNumbers.includes(o.fundNumber));
        });
        const merged = [...selectedPortfolios];
        groupMembers.forEach(m => { if (!merged.some(p => p.fundNumber === m.fundNumber)) merged.push(m); });
        onConfirm(merged);
        setOpen(false);
        setChecked(options.map(() => false));
        setCheckedGroups([]);
    }

    const anyChecked = checked.some(Boolean) || checkedGroups.length > 0;

    return (
        <div className="vp-replicate-wrap" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button className={`vp-replicate-btn${open ? ' open' : ''}`} title="Replicate video to other portfolios">
                <Copy size={12} />
                Replicate
            </button>
            {open && (
                <div className="vp-replicate-popup" onMouseDown={e => e.stopPropagation()}>
                    <div className="vp-replicate-list">
                        {groups.length > 0 && (
                            <>
                                <div className="vp-replicate-section-label">Groups</div>
                                {groups.map(g => (
                                    <label key={g.id} className="vp-replicate-option">
                                        <input
                                            type="checkbox"
                                            checked={checkedGroups.includes(g.id)}
                                            onChange={() => setCheckedGroups(prev =>
                                                prev.includes(g.id) ? prev.filter(id => id !== g.id) : [...prev, g.id]
                                            )}
                                        />
                                        <span>{g.name}</span>
                                    </label>
                                ))}
                                {options.length > 0 && <div className="vp-replicate-section-label" style={{ marginTop: 6 }}>Portfolios</div>}
                            </>
                        )}
                        {options.length === 0 && groups.length === 0
                            ? <span className="vp-replicate-empty">No other portfolios</span>
                            : options.map((opt, idx) => (
                                <label key={idx} className="vp-replicate-option">
                                    <input type="checkbox" checked={checked[idx]} onChange={() => setChecked(c => c.map((v, i) => i === idx ? !v : v))} />
                                    <span>{opt.label}</span>
                                </label>
                            ))
                        }
                    </div>
                    <button className="vp-replicate-confirm" disabled={!anyChecked} onClick={handleConfirm}>Confirm</button>
                </div>
            )}
        </div>
    );
}

const POLL_INTERVAL_MS = 10_000;          // fast phase, right after upload
const POLL_INTERVAL_SLOW_MS = 30_000;     // back-off once captions are taking a while
const POLL_BACKOFF_AFTER_MS = 2 * 60_000; // switch to the slow interval after this long
const POLL_TIMEOUT_MS = 20 * 60_000;      // overall cap while the panel stays open
const BUNNY_STATUS_FINISHED = 4;
const TRANSCRIPTION_FIELD = 'videoTranscription';
// Provenance of the transcription so the server's Bunny webhook knows when it may auto-fill.
// 'auto' = written by upload/auto-seed (safe to overwrite); 'edited' = the user typed or intentionally
// cleared it (webhook must leave it alone). Kept in sync with SystemConstants on the backend.
const TRANSCRIPTION_SOURCE_FIELD = 'videoTranscriptionSource';
const TRANSCRIPTION_SOURCE_AUTO = 'auto';
const TRANSCRIPTION_SOURCE_EDITED = 'edited';
const TRANSCRIPTION_SAVE_DEBOUNCE_MS = 1500;
// Replicate is hidden until the backend fan-out is wired. See note at the JSX call site.
const REPLICATE_ENABLED = false;
const cuesToText = (cues) => (cues || []).map(c => c.text).join('\n\n');
const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

const VideoUploadPanel = ({ fundNumber, fundName, template, templateObj, data, onMarkAsReady, portfoliosState, groupPortfolios, groups, uneditable }) => {
    const [uploadState, setUploadState] = useState('idle');
    const [videoData, setVideoData] = useState(null);
    const [transcriptionLoading, setTranscriptionLoading] = useState(false);
    const [transcriptionText, setTranscriptionText] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [replaceConfirm, setReplaceConfirm] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const tusUploadRef = useRef(null);
    const fileInputRef = useRef();
    const transcriptionRef = useRef();
    const pollRef = useRef(null);
    const pollStartRef = useRef(0);
    const hasLoadedRef = useRef(false);
    const saveTimerRef = useRef(null);
    // Ensures the "edited" marker is flushed to the server once per video session on first keystroke,
    // so the server-side webhook can't overwrite an in-progress edit during the text-save debounce.
    const editedMarkerFlushedRef = useRef(false);
    const remote = useRemote();
    const encodedPortfolio = fundNumber ? btoa(fundNumber) : null;

    // Subscribe to DataController mutations so the panel re-renders when
    // localContentData refreshes (e.g. after an async updateTicker resolves
    // for a newly-selected group). Without this, `data.localContentData`
    // reads are stuck on the previous selection's values until something
    // else triggers a re-render — see "stale localContentData on group
    // switch" plan.
    const [, forceRerender] = useReducer(x => x + 1, 0);
    useEffect(() => {
        if (!data?.addLocalListener) return;
        const listener = () => forceRerender();
        data.addLocalListener(listener);
        return () => { if (data.removeLocalListener) data.removeLocalListener(listener); };
    }, [data]);

    const dataMatchesFund = data?.tickers?.[0] === fundNumber;
    const persistedTranscription = (typeof data?.localContentData?.[TRANSCRIPTION_FIELD] === 'string')
        ? data.localContentData[TRANSCRIPTION_FIELD]
        : '';
    const hasPersistedTranscription = persistedTranscription.length > 0;

    function flushPendingTranscriptionSave() {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
    }

    // `source` defaults to 'edited' so the textarea onChange path (typing, or clearing to empty) always
    // marks the transcript as user-owned. Auto-seed paths pass 'auto' explicitly.
    function persistTranscription(text, source = TRANSCRIPTION_SOURCE_EDITED) {
        if (data?.updateLocalData) {
            data.updateLocalData(TRANSCRIPTION_FIELD, text);
            data.updateLocalData(TRANSCRIPTION_SOURCE_FIELD, source);
        }
        flushPendingTranscriptionSave();
        if (!encodedPortfolio || !template) return;
        saveTimerRef.current = setTimeout(() => {
            saveTimerRef.current = null;
            remote.put(`savev2/${encodedPortfolio}/${template}`, {
                [TRANSCRIPTION_FIELD]: text,
                [TRANSCRIPTION_SOURCE_FIELD]: source,
            });
        }, TRANSCRIPTION_SAVE_DEBOUNCE_MS);
    }

    // On the first keystroke of an edit, commit the 'edited' marker immediately (not debounced) so the
    // server-side webhook — which only auto-fills 'auto' rows — can't clobber the edit during the 1.5s
    // text-save debounce. Fires once per video session (reset on fund/template change, upload, replace).
    function markEditedImmediately() {
        if (data?.updateLocalData) {
            data.updateLocalData(TRANSCRIPTION_SOURCE_FIELD, TRANSCRIPTION_SOURCE_EDITED);
        }
        if (editedMarkerFlushedRef.current || !encodedPortfolio || !template) return;
        editedMarkerFlushedRef.current = true;
        remote.put(`savev2/${encodedPortfolio}/${template}`, {
            [TRANSCRIPTION_SOURCE_FIELD]: TRANSCRIPTION_SOURCE_EDITED,
        });
    }

    useLayoutEffect(() => {
        if (transcriptionRef.current) {
            transcriptionRef.current.style.height = 'auto';
            transcriptionRef.current.style.height = transcriptionRef.current.scrollHeight + 'px';
        }
    }, [transcriptionText]);

    function stopPolling() {
        if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    }

    function abortTusUpload() {
        if (tusUploadRef.current) {
            try { tusUploadRef.current.abort(); } catch (e) { /* ignore */ }
            tusUploadRef.current = null;
        }
    }

    useEffect(() => {
        stopPolling();
        abortTusUpload();
        flushPendingTranscriptionSave();
        setUploadState('idle');
        setVideoData(null);
        setTranscriptionText('');
        setTranscriptionLoading(false);
        setReplaceConfirm(false);
        setUploadProgress(0);
        hasLoadedRef.current = false;
        editedMarkerFlushedRef.current = false;
        return () => {
            stopPolling();
            abortTusUpload();
            flushPendingTranscriptionSave();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fundNumber, template]);

    // Seed the transcription textarea from localContentData, but only once the
    // DataController has caught up to the current fundNumber (`data.tickers[0]`).
    // Otherwise we'd briefly write the previous selection's transcription into
    // the new fund's panel.
    useEffect(() => {
        if (!dataMatchesFund) return;
        setTranscriptionText(persistedTranscription);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataMatchesFund, persistedTranscription]);

    useEffect(() => {
        if (!fundNumber || !template) return;
        if (hasLoadedRef.current) return;
        if (!dataMatchesFund) return;  // wait for DataController to publish the current fund's data
        const localGuid = data?.localContentData?.videoId;
        if (localGuid) {
            hasLoadedRef.current = true;
            loadExistingVideo(localGuid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fundNumber, template, dataMatchesFund, data?.localContentData?.videoId]);

    function applyTranscription(cues) {
        setTranscriptionLoading(false);
        if (hasPersistedTranscription) return;
        const seeded = cuesToText(cues);
        setTranscriptionText(seeded);
        persistTranscription(seeded, TRANSCRIPTION_SOURCE_AUTO);
    }

    function startPolling(guid) {
        stopPolling();
        pollStartRef.current = Date.now();
        scheduleNextTick(guid);
    }

    // Self-rescheduling tick so the interval can back off (10s → 30s) over a longer window.
    function scheduleNextTick(guid) {
        const elapsed = Date.now() - pollStartRef.current;
        const delay = elapsed >= POLL_BACKOFF_AFTER_MS ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_MS;
        pollRef.current = setTimeout(() => tickPolling(guid), delay);
    }

    function tickPolling(guid) {
        if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
            stopPolling();
            setTranscriptionLoading(false);
            return;
        }
        Promise.all([
            remote.get(`bunny/videos/${guid}`),
            remote.get(`bunny/videos/${guid}/transcription`),
        ]).then(([infoRes, transRes]) => {
            const info = unwrap(infoRes);
            const cues = unwrap(transRes) || [];
            if (info) setVideoData(info);

            // Bunny produces captions asynchronously (often after encoding finishes). Keep polling with
            // back-off until they appear or the overall cap is hit, so a slow-caption video still fills
            // while the panel stays open. The server-side webhook is the safety net for when the user leaves.
            if (cues.length > 0) {
                applyTranscription(cues);
                stopPolling();
                return;
            }
            scheduleNextTick(guid);
        }).catch(() => { scheduleNextTick(guid); });
    }

    function loadExistingVideo(guid) {
        setUploadState('uploaded');
        if (hasPersistedTranscription) {
            setTranscriptionText(persistedTranscription);
            setTranscriptionLoading(false);
            remote.get(`bunny/videos/${guid}`)
                .then(infoRes => {
                    const info = unwrap(infoRes);
                    if (!info?.guid) throw new Error('not found');
                    setVideoData(info);
                })
                .catch(() => {
                    setUploadState('idle');
                    setVideoData(null);
                });
            return;
        }

        setTranscriptionLoading(true);
        remote.get(`bunny/videos/${guid}`)
            .then(infoRes => {
                const info = unwrap(infoRes);
                if (!info?.guid) throw new Error('not found');
                setVideoData(info);
                return remote.get(`bunny/videos/${guid}/transcription`).then(transRes => {
                    const cues = unwrap(transRes) || [];
                    if (info.status === BUNNY_STATUS_FINISHED && cues.length > 0) {
                        applyTranscription(cues);
                    } else {
                        startPolling(guid);
                    }
                });
            })
            .catch(() => {
                setUploadState('idle');
                setVideoData(null);
                setTranscriptionLoading(false);
            });
    }

    function persistVideoOnPortfolio(videoId) {
        flushPendingTranscriptionSave();
        editedMarkerFlushedRef.current = false; // fresh video → back to 'auto', allow a future edit-flush
        const uploadedAt = new Date().toISOString();
        const payload = {
            videoId: videoId,
            videoUploadDate: uploadedAt,
            [TRANSCRIPTION_FIELD]: '',
            [TRANSCRIPTION_SOURCE_FIELD]: TRANSCRIPTION_SOURCE_AUTO,
        };
        remote.put(`savev2/${encodedPortfolio}/${template}`, payload);
        if (data?.updateLocalData) {
            data.updateLocalData('videoId', videoId);
            data.updateLocalData('videoUploadDate', uploadedAt);
            data.updateLocalData(TRANSCRIPTION_FIELD, '');
            data.updateLocalData(TRANSCRIPTION_SOURCE_FIELD, TRANSCRIPTION_SOURCE_AUTO);
        }
    }

    function startReplace() {
        stopPolling();
        abortTusUpload();
        flushPendingTranscriptionSave();
        setUploadState('idle');
        setVideoData(null);
        setTranscriptionText('');
        setTranscriptionLoading(false);
        setReplaceConfirm(false);
        setUploadProgress(0);
        editedMarkerFlushedRef.current = false;
    }

    function handleFileDrop(e) {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) uploadVideo(file);
    }

    function handleFileSelect(e) {
        const file = e.target.files[0];
        if (file) uploadVideo(file);
    }

    function uploadVideo(file) {
        const validationError = validateVideoFile(file);
        if (validationError) {
            toastWarning(validationError);
            return;
        }
        setUploadState('uploading');
        setUploadProgress(0);
        const title = `ManagersCommentaryVideo_${fundNumber}_${template}_${new Date().toISOString()}_${file.name}`;

        uploadVideoToBunny(remote, file, {
            title,
            onStart: (upload) => { tusUploadRef.current = upload; },
            onProgress: (sent, total) => {
                if (total > 0) setUploadProgress(Math.round((sent / total) * 100));
            },
        })
            .then(({ guid, info }) => {
                tusUploadRef.current = null;
                hasLoadedRef.current = true;
                persistVideoOnPortfolio(guid);
                // Transcription is handled by Bunny's library auto-transcribe setting;
                // the poll below just displays captions once ready.
                setVideoData(info);
                setUploadState('uploaded');
                setTranscriptionLoading(true);
                startPolling(guid);
                toastSuccess('Video uploaded. System will transcribe it shortly.');
            })
            .catch(() => {
                tusUploadRef.current = null;
                setUploadState('error');
                toastWarning('Video upload failed. Please try again.');
            });
    }

    const otherPortfolios = useMemo(() =>
        (portfoliosState || [])
            .filter(p => p.fundNumber !== fundNumber)
            .map(p => ({ label: p.fundName, ...p })),
        [portfoliosState, fundNumber]
    );

    const groupPreSelected = useMemo(() =>
        groupPortfolios
            ? groupPortfolios.filter(p => p.fundNumber !== fundNumber).map(p => p.fundNumber)
            : null,
        [groupPortfolios, fundNumber]
    );

    const isInGroup = Array.isArray(groupPortfolios) && groupPortfolios.length > 0;
    // "Mark as Ready to Publish" feeds the Pending→Ready pool, which only exists in
    // portfolio_multiple mode. In portfolio_single(_video) there is no Ready pool — publishing
    // goes through the sidebar's review/Publish flow — so the button must be hidden there.
    const sidebarOptions = (templateObj && templateObj.ticker && templateObj.ticker.extSideBarOptions) || [];
    const isMultipleMode = sidebarOptions.includes('portfolio_multiple') || sidebarOptions.includes('portfolio_multiple_video');

    function replicateVideo(selected) {
        if (!videoData) return;
        Promise.all(
            selected.map(p => {
                const encoded = btoa(p.fundNumber);
                return remote.put(`videos/${encoded}/${template}`, {
                    videoId: videoData.guid,
                });
            })
        ).then(() => toastSuccess(`Video replicated to ${selected.length} portfolio${selected.length > 1 ? 's' : ''}.`))
         .catch(() => toastWarning('Replication failed. Please try again.'));
    }

    function handleMarkAsReady() {
        toastSuccess('Marked as Ready to Publish.');
        if (onMarkAsReady) onMarkAsReady();
    }

    function statusBadge() {
        if (uploadState === 'idle')         return <span className="vp-badge vp-badge--idle">No video</span>;
        if (uploadState === 'uploading')    return <span className="vp-badge vp-badge--uploading">Uploading…</span>;
        if (transcriptionLoading)           return <span className="vp-badge vp-badge--transcribing">Transcribing…</span>;
        if (uploadState === 'uploaded')     return <span className="vp-badge vp-badge--ready">Ready</span>;
        if (uploadState === 'error')        return <span className="vp-badge vp-badge--error">Failed</span>;
    }

    return (
        <>
        {process.env.NODE_ENV === 'development1' && (
            <div className="video-dev-toolbar">
                <span className="video-dev-label">Dev</span>
                <button onClick={() => { setUploadState('idle'); setVideoData(null); setTranscriptionLoading(false); }}>Idle</button>
                <button onClick={() => { setUploadState('uploading'); }}>Uploading</button>
                <button onClick={() => {
                    setUploadState('uploaded');
                    setVideoData({ guid: 'dev-guid', videoLibraryId: 0 });
                    setTranscriptionLoading(true);
                }}>Uploaded – transcribing</button>
                <button onClick={() => {
                    setUploadState('uploaded');
                    setVideoData({ guid: 'dev-guid', videoLibraryId: 0 });
                    setTranscriptionLoading(false);
                    setTranscriptionText(DEV_TRANSCRIPTION);
                    if (!hasPersistedTranscription) persistTranscription(DEV_TRANSCRIPTION, TRANSCRIPTION_SOURCE_AUTO);
                }}>Transcription ready</button>
                <button onClick={() => { setUploadState('error'); }}>Error</button>
            </div>
        )}
        <div className="video-upload-panel">
            {showInfoModal && (
                <Modal
                    header="Video Commentary"
                    handleClose={() => setShowInfoModal(false)}
                    confirmButton={{ label: 'Got it', onClick: () => setShowInfoModal(false) }}
                    className="video-upload-panel-modal"
                >
                    <p>Once uploaded, the video will automatically be transcribed. The transcription will be sent directly into the editor below for you to review and edit.</p>
                    <p>The video and transcription will both appear on the investor portal for this portfolio.</p>
                </Modal>
            )}

            {/* ── Slim header — always visible ── */}
            <div className="vp-header">
                <div className="vp-header-left">
                    <Film size={13} />
                    <span className="vp-header-title">Video Commentary</span>
                    {fundName && <span className="vp-header-fund">{fundName}</span>}
                    {statusBadge()}
                    {uploadState === 'uploading' && <LoadingIcon size={12} />}
                </div>
                <div className="vp-header-right">
                    <button className="vp-icon-btn" onClick={() => setShowInfoModal(true)} title="About video commentary">
                        <Info size={14} />
                    </button>
                </div>
            </div>

            <div className="vp-body">
                    {uploadState === 'idle' && (
                        uneditable
                            ? <div className="vp-drop-zone vp-drop-zone--disabled">
                                <span>Editing disabled — move back to Pending to re-enable.</span>
                            </div>
                            : <div
                                className={`vp-drop-zone${dragOver ? ' drag-over' : ''}`}
                                onClick={() => fileInputRef.current.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleFileDrop}
                            >
                                <Upload size={18} />
                                <span>Drop a video here, or <strong>browse</strong></span>
                                <span className="vp-drop-hint">MP4, MOV, MKV, WebM, AVI… · up to 500 MB</span>
                                <input type="file" accept={ACCEPTED_VIDEO_ACCEPT_ATTR} ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
                            </div>
                    )}

                    {uploadState === 'uploading' && (
                        <div className="vp-status-row">
                            <LoadingIcon size={14} />
                            <span>Uploading video… {uploadProgress}%</span>
                        </div>
                    )}

                    {uploadState === 'uploaded' && videoData && (
                        <div className="vp-uploaded">
                            {!uneditable && (
                                <div className="vp-player-row">
                                    {replaceConfirm ? (
                                        <>
                                            <span style={{ fontSize: 12 }}>Replace?</span>
                                            <button className="vp-replace-btn" onClick={startReplace} title="Confirm replace">
                                                <Check size={14} />
                                            </button>
                                            <button className="vp-text-btn" onClick={() => setReplaceConfirm(false)} title="Cancel">
                                                <X size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <button className="vp-replace-btn" onClick={() => setReplaceConfirm(true)}>Replace video</button>
                                    )}
                                    {/*
                                        Replicate is intentionally hidden — no backend API is wired up
                                        to propagate the video id to other portfolios. The
                                        ReplicateButton component, replicateVideo handler,
                                        otherPortfolios memo, and groupPreSelected memo all stay in
                                        place so re-enabling is a one-line flip of REPLICATE_ENABLED,
                                        but it MUST NOT be flipped on without first wiring the
                                        server-side fan-out and re-validating access checks for
                                        group/portfolio replication.
                                    */}
                                    {REPLICATE_ENABLED && (
                                        <ReplicateButton options={otherPortfolios} onConfirm={replicateVideo} preSelectedFundNumbers={groupPreSelected} template={template} groups={groups} />
                                    )}
                                    {!isInGroup && isMultipleMode && (
                                        <button className="vp-ready-btn" disabled={transcriptionLoading} onClick={handleMarkAsReady}>
                                            <CheckCircle size={13} />
                                            Mark as Ready to Publish
                                        </button>
                                    )}
                                </div>
                            )}

                            {videoData.guid && (
                                <div className="vp-thumbnail-section">
                                    <div className="vp-thumbnail-header">
                                        <span className="vp-thumb-label">Portal thumbnail</span>
                                        <span className="vp-thumb-hint">Auto-generated after upload</span>
                                    </div>
                                    <img
                                        className="vp-thumb-single"
                                        src={`/api/bunny/videos/${videoData.guid}/thumbnail`}
                                        alt="Video thumbnail"
                                        style={{ maxWidth: 240, width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 4 }}
                                        onError={e => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}

                            {videoData.guid && (
                                <div className="vp-portal-video-wrap">
                                    <iframe
                                        key={`${videoData.guid}-${videoData.status >= BUNNY_STATUS_FINISHED ? 'ready' : 'processing'}`}
                                        title="Video preview"
                                        src={`https://player.mediadelivery.net/embed/${videoData.videoLibraryId}/${videoData.guid}`}
                                        loading="lazy"
                                        className="vp-portal-video"
                                        style={{ border: 'none', width: '100%', aspectRatio: '16/9' }}
                                        allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
                                        allowFullScreen
                                    />
                                </div>
                            )}

                            <div className="vp-portal-text">
                                {transcriptionLoading && !hasPersistedTranscription
                                    ? <div className="vp-transcription-card-pending"><LoadingIcon size={14} /><span>System is transcribing your video…</span></div>
                                    : <textarea
                                        ref={transcriptionRef}
                                        className="vp-transcription-textarea"
                                        value={transcriptionText}
                                        readOnly={uneditable}
                                        onChange={uneditable ? undefined : (e) => {
                                            const next = e.target.value;
                                            setTranscriptionText(next);
                                            markEditedImmediately();
                                            persistTranscription(next);
                                        }}
                                        placeholder="Transcription will appear here…"
                                        rows={1}
                                      />
                                }
                            </div>
                            {!uneditable && !isInGroup && isMultipleMode && (
                                <div className="vp-bottom-actions">
                                    <button className="vp-ready-btn" disabled={transcriptionLoading} onClick={handleMarkAsReady}>
                                        <CheckCircle size={13} />
                                        Mark as Ready to Publish
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {uploadState === 'error' && (
                        <div className="vp-status-row vp-status-row--error">
                            <span>Upload failed.</span>
                            <button className="vp-text-btn" onClick={() => setUploadState('idle')}>Try again</button>
                        </div>
                    )}
                </div>
        </div>
        </>
    );
};

export default VideoUploadPanel;
